import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Trophy, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center">
              <Trophy className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              GameLink<span className="text-blue-600">OS</span>
            </div>
          </div>
          <div className="text-sm text-slate-500 uppercase tracking-widest">Internal Operations</div>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-8">
            {sent ? (
              <div className="text-center space-y-4" data-testid="forgot-sent">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </div>
                <h1 className="text-xl font-bold text-slate-900">Check your inbox</h1>
                <p className="text-sm text-slate-500">
                  If <span className="font-medium text-slate-700">{email}</span> is registered, a reset link has been sent.
                  The link expires in 60 minutes.
                </p>
                <Link to="/login" className="inline-block text-sm text-blue-600 hover:underline">
                  ← Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <h1 className="text-xl font-bold text-slate-900 mb-1">Reset your password</h1>
                <p className="text-sm text-slate-500 mb-6">
                  Enter the email you use for GameLink OS and we'll send you a reset link.
                </p>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input
                      id="email" type="email" required
                      data-testid="forgot-email-input"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit" disabled={busy}
                    data-testid="forgot-submit-button"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {busy ? "Sending..." : "Send reset link"}
                  </Button>
                </form>
                <div className="mt-6 text-center">
                  <Link to="/login" className="text-sm text-blue-600 hover:underline">
                    ← Back to sign in
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
